import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { forgotPasswordQuery } from "../../Graphql/querries/forgotPasswordQuery";
import {
  ForgotPasswordRequest,
  ForgotPasswordResponse
} from "../../Graphql/types/forgotPassword";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const useForgotPassword = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<ForgotPasswordResponse, Error, ForgotPasswordRequest>({
    mutationKey: ["forgot-password"],

    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started forgot password request",
        buildLogContext(
          "auth",
          "forgot-password",
          // userId/companyId/storeId are not known — user is unauthenticated at this point
          undefined,
          undefined,
          undefined
        )
      );

      return fetchGraphQL<
        ForgotPasswordResponse,
        { input: ForgotPasswordRequest }
      >(forgotPasswordQuery, { input }, setAlert);
    },

    onSuccess: (response) => {
      const { code } = response.forgotPassword;

      // ⚠️ Expected API/business failure
      if (code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Forgot password rejected by API",
          buildLogContext(
            "auth",
            "forgot-password",
            // userId/companyId/storeId not available — user is unauthenticated
            undefined,
            undefined,
            undefined,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        return;
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Forgot password request completed successfully",
        buildLogContext(
          "auth",
          "forgot-password",
          // userId/companyId/storeId not available — user is unauthenticated
          undefined,
          undefined,
          undefined
        )
      );
    },

    onError: (error) => {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Forgot password request failed",
        error,
        buildLogContext(
          "auth",
          "forgot-password",
          // userId/companyId/storeId not available — user is unauthenticated
          undefined,
          undefined,
          undefined
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: error.message
          ? (error.message as ALERT_BANNER_CODE)
          : ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export default useForgotPassword;
